import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { usePlatform } from '@shared/platform'
import { getProfile, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import {
  generateTelegramAuthToken,
  useTelegramAuthToken
} from '../api/telegramAuthApi'

interface UseTelegramAuthFlowParams {
  /** URL для редиректа после успешной авторизации */
  redirectTo: string
}

interface UseTelegramAuthFlowReturn {
  /** Активный токен (null если не запущен) */
  token: string | null
  /** Идёт ли запрос generate-token */
  isStarting: boolean
  /** Текст ошибки (если есть) */
  error: string | null
  /** Запустить флоу: generate → openLink → start polling */
  start: () => Promise<void>
  /** Отменить флоу (закрыть модалку, сбросить токен) */
  cancel: () => void
  /** Колбэк для polling: бэк вернул confirmed — активируем сессию */
  handleConfirmed: () => Promise<void>
  /** Колбэк для polling: токен истёк/использован */
  handleExpired: () => void
}

/**
 * Оркестратор web-стороны авторизации через Telegram.
 * Создаёт токен, открывает бот, поллит статус, активирует сессию.
 */
export const useTelegramAuthFlow = ({ redirectTo }: UseTelegramAuthFlowParams): UseTelegramAuthFlowReturn => {
  const navigate = useNavigate()
  const platform = usePlatform()
  const queryClient = useQueryClient()
  const invalidateProfile = useInvalidateProfile()
  const setProfile = useAppStore((s) => s.setProfile)
  const setAuthStatus = useAppStore((s) => s.setAuthStatus)
  const [token, setToken] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = async () => {
    setIsStarting(true)
    setError(null)

    // В браузере popup блокируется, если window.open вызвать ПОСЛЕ await
    // (теряется user-gesture от клика). Поэтому открываем окно синхронно
    // здесь же, а реальный URL подставляем, когда придёт токен.
    // В TG это не нужно — там platform.openLink нативный, без блокировки.
    const popup = platform.isWeb()
      ? window.open('about:blank', '_blank')
      : null

    try {
      const res = await generateTelegramAuthToken()
      if (popup && !popup.closed) {
        popup.location.replace(res.link)
      } else {
        // Окно не открылось (заблокировано/закрыто) или мы в TG —
        // открываем штатным способом.
        platform.openLink(res.link)
      }
      setToken(res.token)
    } catch {
      popup?.close()
      setError('Не удалось начать вход через Telegram. Попробуйте ещё раз.')
    } finally {
      setIsStarting(false)
    }
  }

  const cancel = () => {
    setToken(null)
    setError(null)
  }

  const handleConfirmed = async () => {
    if (!token) return
    try {
      await useTelegramAuthToken(token)
      const profile = await getProfile()
      setProfile(profile)
      setAuthStatus('success')
      await invalidateProfile()
      await queryClient.invalidateQueries(['subscription'])
      setToken(null)
      navigate(redirectTo, { replace: true })
    } catch {
      setToken(null)
      setError('Не удалось завершить авторизацию. Попробуйте ещё раз.')
    }
  }

  const handleExpired = () => {
    setToken(null)
    setError('Время авторизации закончилось. Попробуйте снова.')
  }

  return {
    token, isStarting, error,
    start, cancel, handleConfirmed, handleExpired
  }
}
